/**
 * EnemyHolo — the trading-card hero art (BL-59, ART §9 + §6.8).
 *
 * The Pokémon principle: a lush, elaborated **card hero** vs. the simple in-game
 * token. The GRID keeps its plain `EnemyShapes` silhouette (unchanged); the CARD
 * shows the SAME recognizable threat made **dimensional + alive** — a procedural
 * *holo* of the abstract form, never a raster illustration (signed North Star
 * §3/§5/§7.3: 100% procedural, NO raster/AI entity art).
 *
 * The art is the REAL engine enemy: the identical `ENEMY_SHAPES[shape]` path the
 * arena draws, filled with the arena's own `emissiveFill(deriveRamp(colour))`,
 * then **elaborated** with depth (an extrusion stack), a bright rim-light, a
 * pulsing white-hot core, and a moving specular catch-light — and **escalated by
 * rarity**: BASIC is clean (lit silhouette + rim) → each tier adds a living foil
 * field (radial rays + drifting data-motes), orbiting shard rings, a containment
 * cage, a rotating cipher ring and a scanning iris, up to the LEGENDARY apex.
 * SECRET is the cloaked read (a redacted, scan-revealed silhouette).
 *
 * Bloomed through the ONE shared `UIBloom` (`getSharedUIBloom()` — the
 * ProBar/SkillCard/EnemyReveal per-canvas pattern; no per-holo WebGL context).
 * Animation is keyed off `time` (never `Math.random()` for the silhouette, so
 * the identity is stable); motes use bounded render-only randomness. Everything
 * is `isReducedMotion()`-gated: reduced → a single static rich frame (lit, rim,
 * core, foil field — just no rotation / catch-light / motes / iris sweep).
 *
 * Per-canvas + null-ctx/headless safe: WebGL absent (jsdom / BL-44) → the raw
 * `emissiveFill` material (still lit) shows; no ctx → a silent no-op.
 *
 * The WebGL-3D apex (a real GPU enemy core) is intentionally deferred — the
 * owner's signed 2026-06-06 concept renders the detailed hero as this rich,
 * genuinely-dimensional 2D holo (`docs/card_system_concept.html`). The seam is
 * clean: a future `boss/legendary` rung can mount alongside this without changing
 * the `TradingCard` contract.
 */
import { ENEMY_SHAPES, buildDiamond, buildPhantom } from "../rendering/EnemyShapes.ts";
import { displayDpr } from "../utils/displayDpr.ts";
import { emissiveFill, deriveRamp, type Ramp } from "../rendering/EmissiveMaterial.ts";
import { getSharedUIBloom } from "./ProBar.ts";
import { isReducedMotion } from "../engine/MotionSettings.ts";
import { UI_FOIL } from "./uiTokens.ts";
import { COLORS } from "../core/constants.ts";

/** The craft tiers — mirror the `TradingCard` rarity ladder. */
export type HoloTier = "basic" | "rare" | "epic" | "legendary" | "secret";

/** Craft rank → which layers light up (the §🎨 escalation). */
const RANK: Record<HoloTier, number> = {
  basic: 0,
  rare: 1,
  epic: 2,
  secret: 2,
  legendary: 3,
};

export interface EnemyHoloOptions {
  /** The `EnemyShapes` silhouette family (dart/block/cluster/worm/…). */
  shape: string;
  /** The enemy's central-palette identity colour. */
  colour: string;
  /** The rarity craft tier. */
  tier: HoloTier;
  /** Subject — `enemy` renders the `EnemyShapes` silhouette; `shield` renders the
   *  faceted hex-cage (the `EnemyRenderer.drawShield` geometry) as the hero. */
  subject?: "enemy" | "shield";
}

/** How far the holo canvas overscans its host so the bloom + the frame-breaking
 *  hero can bleed past the art-window edge (the owner's "über die Außenkanten"). */
const OVERSCAN = 18;

interface Mote {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  r: number;
  gold: boolean;
}

export class EnemyHolo {
  /** The visible canvas — mounts into the card's art window. */
  readonly el: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D | null;
  private readonly shape: string;
  private readonly ramp: Ramp;
  private readonly tier: HoloTier;
  private readonly rank: number;
  private readonly subject: "enemy" | "shield";
  private raf = 0;
  private destroyed = false;
  private t0 = now();
  private motes: Mote[] = [];
  /** Render-frame counter — surfaced for the gate-doc ms readout / tests. */
  frameCount = 0;

  constructor(opts: EnemyHoloOptions) {
    this.shape = opts.shape;
    this.ramp = deriveRamp(opts.colour);
    this.tier = opts.tier;
    this.rank = RANK[opts.tier] ?? 0;
    this.subject = opts.subject ?? "enemy";
    this.el = document.createElement("canvas");
    this.el.className = "tc-holo-canvas";
    this.el.setAttribute("aria-hidden", "true");
    this.ctx = this.el.getContext("2d");
  }

  /** Begin the living render (or a single static frame under reduced-motion). */
  start(): void {
    if (this.destroyed) return;
    if (isReducedMotion()) {
      this.drawStatic();
      return;
    }
    this.t0 = now();
    this.loop();
  }

  /** A single rich resting frame (the reduced-motion / headless read). */
  drawStatic(): void {
    this.draw(0.6, 1);
  }

  /** ~30 fps throttled loop — transient + single card, so cheap. */
  private loop(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    const tick = (): void => {
      if (this.destroyed || isReducedMotion()) return;
      const elapsed = now() - this.t0;
      // A brief entrance flare that decays to the resting intensity.
      const k = Math.min(1, elapsed / 460);
      const flare = 1 + 0.5 * (1 - k) * Math.sin(k * Math.PI);
      this.draw(elapsed / 1000, 1 + Math.max(0, flare - 1));
      this.raf = requestAnimationFrame(() => {
        window.setTimeout(() => this.loop(), 33);
      });
    };
    this.raf = requestAnimationFrame(tick);
  }

  /** Draw one holo frame: foil field → elaborated hero → bloom. Null-ctx safe. */
  private draw(time: number, intensity: number): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const host = this.el.parentElement;
    const cw = host?.clientWidth ?? 0;
    const ch = host?.clientHeight ?? 0;
    if (cw <= 0 || ch <= 0) return;
    this.frameCount++;

    const dpr = Math.max(
      1,
      displayDpr(),
    );
    const W = cw + OVERSCAN * 2;
    const H = ch + OVERSCAN * 2;
    this.el.width = Math.round(W * dpr);
    this.el.height = Math.round(H * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);

    const cx = W / 2;
    const cy = H / 2;
    const r = Math.min(cw, ch) * 0.27;
    const ramp = this.ramp;
    const reduced = isReducedMotion();
    const secret = this.tier === "secret";

    // ── 1 · ambient backing glow (all tiers) ──────────────────────────────────
    radial(ctx, ramp.core, r * 2.7, cx, cy, 0.16 * intensity);
    radial(ctx, ramp.mid, r * 1.9, cx, cy, 0.28 * intensity);

    // ── 2 · living foil field — radial scanline rays behind the hero (rank≥1) ──
    if (this.rank >= 1) {
      const rayRot = reduced ? 0.2 : time * 0.16;
      const rays = UI_FOIL.rayCount;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(rayRot);
      ctx.globalCompositeOperation = "lighter";
      ctx.strokeStyle = ramp.core;
      ctx.lineWidth = 1;
      for (let i = 0; i < rays; i++) {
        const a = (i / rays) * Math.PI * 2;
        const r1 = r * 1.5;
        const r2 = r * (i % 4 ? 2.4 : 2.9);
        ctx.globalAlpha = (i % 2 ? 0.06 : 0.1) * intensity;
        ctx.beginPath();
        ctx.moveTo(Math.cos(a) * r1, Math.sin(a) * r1);
        ctx.lineTo(Math.cos(a) * r2, Math.sin(a) * r2);
        ctx.stroke();
      }
      ctx.restore();
      ctx.globalAlpha = 1;
    }

    // The hero floats (rank≥1) — a slow rise the rays don't share (parallax).
    const float = reduced || this.rank < 1 ? 0 : Math.sin(time * 1.1) * r * 0.08;
    const spin = reduced || this.rank < 1 ? 0 : Math.sin(time * 0.5) * 0.12;

    ctx.save();
    ctx.translate(cx, cy + float);

    // ── 3 · epic+ elaboration drawn UNDER the core: cage + cipher ring ─────────
    if (this.rank >= 2 && !secret) {
      this.drawCipherRing(ctx, r, reduced ? 0 : time, intensity);
      this.drawCage(ctx, r, reduced ? 0 : time, intensity);
    }
    if (this.rank >= 2) {
      this.drawOrbitShards(ctx, r, reduced ? 0 : time, ramp, intensity);
    }

    // ── 4 · the recognizable hero — the REAL EnemyShapes silhouette ────────────
    ctx.save();
    ctx.rotate(spin);
    if (this.subject === "shield") {
      this.drawShieldHero(ctx, r, reduced ? 0 : time, intensity);
      ctx.restore(); // spin
      ctx.restore(); // translate to hero centre
      if (!reduced) this.drawMotes(ctx, cx, cy, r, intensity);
      this.bloomOut(ctx, dpr);
      return;
    }
    const build =
      this.shape === "phantom"
        ? buildPhantom
        : (ENEMY_SHAPES[this.shape] ?? buildDiamond);

    // extrusion stack behind the face → fake thickness / bevel (dimensional).
    const depth = Math.max(2, r * 0.18);
    for (let s = 4; s >= 1; s--) {
      const d = (depth * s) / 4;
      ctx.save();
      ctx.translate(d * 0.5, d);
      ctx.beginPath();
      build(ctx, 0, 0, r, time, -Math.PI / 2);
      ctx.fillStyle = ramp.rim;
      ctx.globalAlpha = 0.85;
      ctx.fill();
      ctx.restore();
    }

    // the lit front face (secret → cloaked: dim).
    ctx.beginPath();
    build(ctx, 0, 0, r, time, -Math.PI / 2);
    ctx.fillStyle = emissiveFill(ctx, 0, 0, r, ramp);
    ctx.globalAlpha = secret ? 0.32 : 1;
    ctx.fill();
    ctx.globalAlpha = 1;

    // a crisp inner rim-light (the arena's BL-27 read) — a smaller re-fill.
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    ctx.globalAlpha = (secret ? 0.18 : 0.4) * Math.min(1.5, intensity);
    ctx.beginPath();
    build(ctx, 0, 0, r * 0.82, time, -Math.PI / 2);
    ctx.fillStyle = ramp.core;
    ctx.fill();
    ctx.restore();

    // a bright hot-edge stroke around the silhouette.
    ctx.beginPath();
    build(ctx, 0, 0, r, time, -Math.PI / 2);
    ctx.lineWidth = Math.max(1, r * 0.04);
    ctx.strokeStyle = ramp.core;
    ctx.globalAlpha = secret ? 0.4 : Math.min(1, 0.5 + intensity * 0.25);
    ctx.stroke();
    ctx.globalAlpha = 1;
    ctx.restore(); // spin

    // ── 4b · the per-shape CARD CUE — the dossier echo of the threat's mechanic
    //         (mirrors EnemyRenderer.drawTypeCue; drawn at hero centre, facing up).
    this.drawCardCue(ctx, r, reduced ? 0 : time, intensity);

    // ── 5 · a pulsing white-hot inner core (all but the cloaked secret) ────────
    if (!secret) {
      const pulse = reduced ? 0.8 : 0.7 + Math.sin(time * 3) * 0.3;
      radial(ctx, "#ffffff", r * 0.5 * pulse, 0, 0, 0.7 * intensity);
      ctx.fillStyle = "#ffffff";
      ctx.globalAlpha = 0.85 * Math.min(1, intensity);
      ctx.beginPath();
      ctx.arc(0, 0, r * 0.14, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    // ── 6 · the moving specular catch-light (rank≥1) — the "turn in the light" ─
    if (this.rank >= 1 && !reduced) {
      const la = time * 1.3;
      radial(
        ctx,
        "#ffffff",
        r * 0.42,
        Math.cos(la) * r * 0.5,
        Math.sin(la) * r * 0.5,
        0.45 * intensity,
      );
    }

    // ── 7 · the legendary scanning iris — a bright sweep clipped to the body ───
    if ((this.rank >= 3 || secret) && !reduced) {
      const sy = Math.sin(time * 1.6) * r * 0.9;
      ctx.save();
      ctx.beginPath();
      ctx.arc(0, 0, r * 0.98, 0, Math.PI * 2);
      ctx.clip();
      ctx.globalCompositeOperation = "lighter";
      ctx.strokeStyle = "#ffffff";
      ctx.globalAlpha = 0.5;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(-r, sy);
      ctx.lineTo(r, sy);
      ctx.stroke();
      ctx.restore();
    }

    ctx.restore(); // translate to hero centre

    // ── 8 · drifting data-motes (additive, bloomed) — count scales by rank ─────
    if (!reduced && this.rank >= 1) {
      this.drawMotes(ctx, cx, cy, r, intensity);
    }

    // ── 9 · route the hot edges through the ONE shared WebGL bloom ─────────────
    this.bloomOut(ctx, dpr);
  }

  /** Composite the canvas back through the ONE shared `UIBloom` (the arena GLSL).
   *  No-op when WebGL is unavailable (headless → the raw lit material shows). */
  private bloomOut(ctx: CanvasRenderingContext2D, dpr: number): void {
    const bloom = getSharedUIBloom();
    if (!bloom) return;
    bloom.setDpr(dpr);
    const out = bloom.process(this.el);
    if (!out) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = Math.min(1, 0.6 + this.rank * 0.13);
    ctx.drawImage(out, 0, 0, this.el.width, this.el.height);
    ctx.globalAlpha = 1;
  }

  /**
   * Per-shape CARD CUE — the dossier echo of the in-grid `EnemyRenderer.drawTypeCue`,
   * so the card tells the threat's story (not just its silhouette). Drawn at the
   * hero centre (local 0,0), heading up (the hero's facing). Same per-shape switch
   * shape as the arena cue; only the shapes that earn a dossier flourish get one.
   */
  private drawCardCue(
    ctx: CanvasRenderingContext2D,
    r: number,
    time: number,
    intensity: number,
  ): void {
    if (this.shape === "block") this.drawArmorCardCue(ctx, r, time, intensity);
    else if (this.shape === "ram") this.drawBreachCardCue(ctx, r, time, intensity);
  }

  /** Rootkit (`block`) — the cyan armour containment ring: echoes the in-grid
   *  armour aura so the card reads as the armoured tank (card ↔ token match). */
  private drawArmorCardCue(
    ctx: CanvasRenderingContext2D,
    r: number,
    time: number,
    intensity: number,
  ): void {
    const cyan = COLORS.ICE_CYAN;
    const k = Math.min(1, intensity);
    const breathe = 0.5 + Math.sin(time * 1.6) * 0.5;
    radial(ctx, cyan, r * 1.7, 0, 0, (0.18 + breathe * 0.1) * k);
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    for (let i = 0; i < 2; i++) {
      const rr = r * (1.28 + i * 0.16) * (1 + breathe * 0.03);
      ctx.strokeStyle = cyan;
      ctx.lineWidth = i === 0 ? 2.4 : 1.1;
      ctx.globalAlpha = (i === 0 ? 0.65 : 0.32) * (0.7 + breathe * 0.3) * k;
      ctx.beginPath();
      ctx.arc(0, 0, rr, 0, Math.PI * 2);
      ctx.stroke();
    }
    const rot = time * 0.4;
    for (let i = 0; i < 6; i++) {
      const a = rot + (i / 6) * Math.PI * 2;
      const bx = Math.cos(a) * r * 1.28;
      const by = Math.sin(a) * r * 1.28;
      radial(ctx, cyan, 6, bx, by, 0.55 * k);
      const d = 2.6;
      ctx.fillStyle = cyan;
      ctx.globalAlpha = 0.85 * k;
      ctx.beginPath();
      ctx.moveTo(bx, by - d);
      ctx.lineTo(bx + d, by);
      ctx.lineTo(bx, by + d);
      ctx.lineTo(bx - d, by);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
    ctx.globalAlpha = 1;
  }

  /** Firewall Breaker (`ram`) — the breach read: a cracking SENTINEL-green
   *  firewall arc ahead of the nose (your wall) + a hot impact flare + spark
   *  shards, so the card tells the "tears into your wall" story the wedge didn't. */
  private drawBreachCardCue(
    ctx: CanvasRenderingContext2D,
    r: number,
    time: number,
    intensity: number,
  ): void {
    const up = -Math.PI / 2;
    const impact = 0.5 + Math.abs(Math.sin(time * 4)) * 0.5;
    const k = Math.min(1, intensity);
    const nx = 0;
    const ny = -r * 1.15;
    ctx.save();
    ctx.strokeStyle = COLORS.SENTINEL_GREEN; // green = YOUR firewall being breached
    ctx.globalAlpha = (0.38 + impact * 0.32) * k;
    ctx.lineWidth = Math.max(1.6, r * 0.07);
    ctx.beginPath();
    ctx.arc(0, 0, r * 1.55, up - 0.62, up + 0.62);
    ctx.stroke();
    ctx.lineWidth = 1;
    ctx.globalAlpha = (0.5 + impact * 0.3) * k;
    for (const off of [-0.34, -0.02, 0.3]) {
      const a = up + off;
      ctx.beginPath();
      ctx.moveTo(Math.cos(a) * r * 1.44, Math.sin(a) * r * 1.44);
      ctx.lineTo(Math.cos(a) * r * 1.68, Math.sin(a) * r * 1.68);
      ctx.stroke();
    }
    ctx.restore();
    radial(ctx, "#ffffff", r * 0.5 * (0.7 + impact * 0.6), nx, ny, 0.55 * k);
    radial(ctx, this.ramp.mid, r * 0.95, nx, ny, 0.4 * k);
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    ctx.strokeStyle = this.ramp.mid;
    ctx.lineWidth = Math.max(1, r * 0.05);
    for (let i = 0; i < 5; i++) {
      const a = up + (i - 2) * 0.42;
      const d0 = r * 1.12;
      const d1 = r * (1.28 + impact * 0.55);
      ctx.globalAlpha = (0.28 + impact * 0.42) * k;
      ctx.beginPath();
      ctx.moveTo(Math.cos(a) * d0, Math.sin(a) * d0);
      ctx.lineTo(Math.cos(a) * d1, Math.sin(a) * d1);
      ctx.stroke();
    }
    ctx.restore();
    ctx.globalAlpha = 1;
  }

  /** Orbiting diamond shards (the grid boss's signature, generalised). */
  private drawOrbitShards(
    ctx: CanvasRenderingContext2D,
    r: number,
    time: number,
    ramp: Ramp,
    intensity: number,
  ): void {
    const n = this.rank >= 3 ? 6 : 4;
    for (let i = 0; i < n; i++) {
      const a = time * 0.5 + (i / n) * Math.PI * 2;
      const dist = r * 1.18;
      const dx = Math.cos(a) * dist;
      const dy = Math.sin(a) * dist;
      const d = r * 0.22;
      // a crackling energy arc back to the core.
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      ctx.strokeStyle = ramp.core;
      ctx.globalAlpha = (0.12 + Math.abs(Math.sin(time * 5 + i)) * 0.3) * intensity;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(dx, dy);
      ctx.lineTo(0, 0);
      ctx.stroke();
      ctx.restore();
      // the shard.
      ctx.beginPath();
      ctx.moveTo(dx, dy - d);
      ctx.lineTo(dx + d * 0.65, dy);
      ctx.lineTo(dx, dy + d);
      ctx.lineTo(dx - d * 0.65, dy);
      ctx.closePath();
      ctx.fillStyle = emissiveFill(ctx, dx, dy, d, ramp);
      ctx.fill();
    }
  }

  /** A counter-rotating containment cage — six clipped arcs with bolt vertices. */
  private drawCage(
    ctx: CanvasRenderingContext2D,
    r: number,
    time: number,
    intensity: number,
  ): void {
    ctx.save();
    ctx.rotate(-time * 0.22);
    ctx.strokeStyle = mixToward(this.ramp.mid, "#000000");
    ctx.lineWidth = 2.2;
    ctx.globalAlpha = 0.7 * Math.min(1.3, intensity);
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      ctx.beginPath();
      ctx.arc(0, 0, r * 1.45, a + 0.18, a + 0.92);
      ctx.stroke();
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      ctx.fillStyle = this.ramp.core;
      ctx.globalAlpha = 0.6;
      ctx.beginPath();
      ctx.arc(Math.cos(a + 0.18) * r * 1.45, Math.sin(a + 0.18) * r * 1.45, 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
    ctx.restore();
    ctx.globalAlpha = 1;
  }

  /** A slowly-rotating cipher ring of tick marks — the "classified / scanned" read. */
  private drawCipherRing(
    ctx: CanvasRenderingContext2D,
    r: number,
    time: number,
    intensity: number,
  ): void {
    ctx.save();
    ctx.rotate(time * 0.15);
    ctx.strokeStyle = mixToward(this.ramp.mid, "#ffffff");
    ctx.lineWidth = 1;
    ctx.globalAlpha = 0.4 * Math.min(1.3, intensity);
    const n = 28;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      const r1 = r * 1.62;
      const r2 = r * (i % 4 ? 1.72 : 1.84);
      ctx.beginPath();
      ctx.moveTo(Math.cos(a) * r1, Math.sin(a) * r1);
      ctx.lineTo(Math.cos(a) * r2, Math.sin(a) * r2);
      ctx.stroke();
    }
    ctx.restore();
    ctx.globalAlpha = 1;
  }

  /** The Shield hero — a faceted, rotating **hex-cage** around a protected core
   *  (the `EnemyRenderer.drawShield` geometry, elaborated for the card). */
  private drawShieldHero(
    ctx: CanvasRenderingContext2D,
    r: number,
    time: number,
    intensity: number,
  ): void {
    const ramp = this.ramp;
    const Rs = r * 1.3;
    const rot = time * 0.5;

    radial(ctx, ramp.mid, Rs * 1.4, 0, 0, 0.3 * intensity);

    const vx: number[] = [];
    const vy: number[] = [];
    for (let i = 0; i < 6; i++) {
      const a = rot + (i * Math.PI) / 3 - Math.PI / 6;
      vx[i] = Math.cos(a) * Rs;
      vy[i] = Math.sin(a) * Rs;
    }

    // outer cage chords (bright filament).
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    ctx.strokeStyle = ramp.core;
    ctx.lineWidth = 2;
    ctx.globalAlpha = 0.6 * Math.min(1.3, intensity);
    for (let i = 0; i < 6; i++) {
      const j = (i + 1) % 6;
      ctx.beginPath();
      ctx.moveTo(vx[i]!, vy[i]!);
      ctx.lineTo(vx[j]!, vy[j]!);
      ctx.stroke();
    }
    ctx.restore();

    // inner plate rim hexagon.
    ctx.strokeStyle = ramp.mid;
    ctx.lineWidth = 1;
    ctx.globalAlpha = 0.5;
    const ri = r * 0.95;
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const a = rot + (i * Math.PI) / 3 - Math.PI / 6;
      const x = Math.cos(a) * ri;
      const y = Math.sin(a) * ri;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.stroke();
    ctx.globalAlpha = 1;

    // vertex bolts (filled diamonds) + a glow seed.
    for (let i = 0; i < 6; i++) {
      const x = vx[i]!;
      const y = vy[i]!;
      radial(ctx, ramp.core, 5, x, y, 0.7 * intensity);
      ctx.fillStyle = ramp.core;
      const d = 3;
      ctx.beginPath();
      ctx.moveTo(x, y - d);
      ctx.lineTo(x + d, y);
      ctx.lineTo(x, y + d);
      ctx.lineTo(x - d, y);
      ctx.closePath();
      ctx.fill();
    }

    // the protected core — a small emissive diamond + a white pulse.
    ctx.fillStyle = emissiveFill(ctx, 0, 0, r * 0.4, ramp);
    ctx.beginPath();
    ctx.moveTo(0, -r * 0.4);
    ctx.lineTo(r * 0.3, 0);
    ctx.lineTo(0, r * 0.4);
    ctx.lineTo(-r * 0.3, 0);
    ctx.closePath();
    ctx.fill();
    const pulse = 0.7 + Math.sin(time * 3) * 0.3;
    radial(ctx, "#ffffff", r * 0.3 * pulse, 0, 0, 0.6 * intensity);
  }

  /** Drifting gold / core data-motes (additive, bloom fuel). Bounded count. */
  private drawMotes(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    r: number,
    intensity: number,
  ): void {
    const cap = Math.round(UI_FOIL.moteCap * (0.4 + this.rank * 0.25));
    if (this.motes.length < cap && Math.random() < 0.4) {
      this.motes.push({
        x: cx + (Math.random() - 0.5) * r,
        y: cy + r * 0.6,
        vx: (Math.random() - 0.5) * 0.4,
        vy: -(0.3 + Math.random() * 0.7),
        life: 0,
        max: 50 + Math.random() * 60,
        r: 1 + Math.random() * 1.6,
        gold: this.rank >= 3 && Math.random() < 0.5,
      });
    }
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    this.motes = this.motes.filter((m) => {
      m.life++;
      m.x += m.vx;
      m.y += m.vy;
      m.vy *= 0.99;
      if (m.life >= m.max) return false;
      const a = Math.sin((m.life / m.max) * Math.PI) * 0.8 * intensity;
      const col = m.gold ? "#FFE9A8" : this.ramp.core;
      radial(ctx, col, m.r * 4, m.x, m.y, a);
      return true;
    });
    ctx.restore();
  }

  destroy(): void {
    this.destroyed = true;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.motes = [];
    this.el.remove();
  }
}

// ─── Local helpers ────────────────────────────────────────────────────────────

/** A soft additive radial glow centred at (x, y). Resets alpha + comp on exit. */
function radial(
  ctx: CanvasRenderingContext2D,
  colour: string,
  r: number,
  x: number,
  y: number,
  alpha: number,
): void {
  if (r <= 0 || alpha <= 0) return;
  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, colour);
  g.addColorStop(0.4, withAlpha(colour, 0.5));
  g.addColorStop(1, "transparent");
  ctx.fillStyle = g;
  ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** Mix a #rrggbb hex toward a target hex by t (render-only; no central hex). */
function mixToward(hex: string, target: string, t = 0.2): string {
  const a = parseHex(hex);
  const b = parseHex(target);
  return `#${ch(a[0] + (b[0] - a[0]) * t)}${ch(a[1] + (b[1] - a[1]) * t)}${ch(
    a[2] + (b[2] - a[2]) * t,
  )}`;
}

/** A #rrggbb hex as an rgba() string at the given alpha (for gradient stops). */
function withAlpha(hex: string, alpha: number): string {
  const [r, g, b] = parseHex(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function parseHex(hex: string): [number, number, number] {
  let h = hex.replace(/^#/, "");
  if (h.length === 3) h = h.replace(/(.)/g, "$1$1");
  const n = parseInt(h, 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

function ch(v: number): string {
  return Math.max(0, Math.min(255, Math.round(v)))
    .toString(16)
    .padStart(2, "0");
}

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}
